// Produktová karta (issue #10, 02-komponenty.md §2 a §3).
//
// Enhancement nad fixním Shoptet DOM `div.p[data-micro=product]`:
//   • obalí `a.image` do `.bs-card__media` (jediný nový wrapper)
//   • přesune nativní `.prices` do fotky jako cenovou pilulku
//   • z nativních `span.flag` udělá badgy; příznak TIP se NEKRESLÍ (nese 85 %
//     katalogu) — Bestseller samolepku dává INDEX (`mountSticker`, viz níž)
//   • badge slevy dopočítá z nativního `.price-save`
//   • hodnocení z `.stars-wrapper[data-micro-rating-*]` složí na pilulku `★ 3,8 (5)`
//     do pravého horního rohu fotky (redesign `design_handoff_category/`)
//   • dostupnost přeskládá na „ukazatel zásoby": řádek `Skladem N ks` +
//     `Expedujeme …` a pod ním proužek zásoby
//   • CTA dostane ikonu a cenu („Do košíku za 1 290 Kč")
//
// Zásady: žádná vymyšlená data (chybí-li parametry v katalogu, nekreslíme je),
// pořadí prvků řeší CSS `order`, add-to-cart form se NEPŘEPISUJE — košík
// obsluhuje výhradně nativní Shoptet JS (shoptet.cartShared.addToCart).
//
// Idempotentní (`data-bs-card`), po AJAX append (loadMore) dorovnává MutationObserver.

import { icon } from './icon.js';
import { listingDispatch } from './deliveryTimeline.js';
import { byLabelLength, isActionFlag, isClearanceFlag, oneActionFlag } from './flagOrder.js';
import { saleLabel } from './campaign.js';
import L from '../locale/cs/productCard.js';

const CARD_SELECTOR = '.p[data-micro="product"]';

const LABEL_BESTSELLER = L.bestseller;
const LABEL_IN_STOCK = L.inStock;

function text(el) {
  return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

// 1 recenze / 2–4 recenze / 5+ recenzí
function reviewsLabel(n) {
  return L.reviewsWord(n);
}

function formatRating(value) {
  return (Math.round(value * 10) / 10).toFixed(1).replace('.', ',');
}

// „ (–41 %)" → „−41 %" (nativní text nese pomlčku i závorky)
function discountLabel(card) {
  const save = card.querySelector('.price-save');
  const m = text(save).match(/(\d+)\s*%/);
  return m ? '−' + m[1] + ' %' : null;
}

function badge(cls, label) {
  const span = document.createElement('span');
  span.className = 'bs-badge' + (cls ? ' ' + cls : '');
  span.textContent = label;
  return span;
}

function buildSticker() {
  const wrap = document.createElement('span');
  wrap.className = 'bs-card__sticker';
  const star = document.createElement('span');
  star.className = 'bs-card__sticker-star';
  star.textContent = '★';
  const label = document.createElement('span');
  label.className = 'bs-card__sticker-label';
  label.textContent = LABEL_BESTSELLER;
  wrap.appendChild(star);
  wrap.appendChild(label);
  wrap.setAttribute('aria-hidden', 'true');
  return wrap;
}

/**
 * Samolepka Bestseller na kartě. Zdroj je INDEX (`/specs` → `bestseller: true`
 * = top výchozí kategorie dle tržby za 90 dní, indexer/bestsellers.mjs), ne
 * nativní příznak TIP — ten nese 85 % katalogu, takže by visela skoro všude
 * (rozhodnutí Tomáše 2026-08-26). Volá cardExtras.js / specChips.js po odpovědi
 * API; karta z indexu (výsledky hledání) a harnessy ji dostanou rovnou z
 * atributu `data-bs-bestseller="1"`. Idempotentní; mini karta ji nedostane.
 */
export function mountSticker(card) {
  if (!card || card.classList.contains('bs-card--mini')) return;
  const media = card.querySelector('.bs-card__media');
  if (!media || media.querySelector('.bs-card__sticker')) return;
  media.appendChild(buildSticker());
}

// Nativní vlaječky → naše badgy. `.flag-discount` (blok „Nejprodávanější")
// nese cenu, ne text štítku — sleva se bere z `.price-save`. TIP se přeskakuje
// celý: ani badge, ani samolepka (viz mountSticker).
function buildBadges(card, media) {
  const box = document.createElement('div');
  box.className = 'bs-card__badges';

  const disc = discountLabel(card);
  if (disc) box.appendChild(badge('', disc));

  const flags = Array.prototype.filter.call(card.querySelectorAll('.flags .flag'), function (flag) {
    return !flag.classList.contains('flag-discount') && !flag.classList.contains('flag-tip') && text(flag);
  });
  // jeden akční štítek (výprodej skladu před Slevoakcí); po dobu kampaně nese
  // akční nabídka název deštníku (mock M8 b), výprodej skladu je vždy žlutý
  const saleText = saleLabel();
  byLabelLength(oneActionFlag(flags), function (flag) {
    return (isActionFlag(flag) && saleText) || text(flag);
  }).forEach(function (flag) {
    if (isClearanceFlag(flag)) box.appendChild(badge('bs-badge--clearance', text(flag)));
    else if (isActionFlag(flag)) box.appendChild(badge('bs-badge--ink', saleText || text(flag)));
    else box.appendChild(badge(flag.classList.contains('flag-new') ? 'bs-badge--outline' : 'bs-badge--ink', text(flag)));
  });

  if (box.children.length) media.appendChild(box);
}

// Hodnocení = pilulka `★ 3,8 (5)` v pravém horním rohu fotky. Mini karta
// (blok „Nejprodávanější") ji nemá — návrh na ní hodnocení nekreslí.
function buildRating(card, media) {
  const wrap = card.querySelector('.stars-wrapper');
  if (!wrap || !media) return;
  const value = parseFloat(wrap.getAttribute('data-micro-rating-value'));
  const count = parseInt(wrap.getAttribute('data-micro-rating-count'), 10);
  if (!isFinite(value) || value <= 0) return;

  const pill = document.createElement('span');
  pill.className = 'bs-card__rate';
  pill.innerHTML =
    '<span class="bs-card__rate-star" aria-hidden="true">★</span>' +
    '<span class="bs-card__rate-value"></span>' +
    '<span class="bs-card__rate-count"></span>';
  pill.querySelector('.bs-card__rate-value').textContent = formatRating(value);
  if (isFinite(count) && count > 0) {
    pill.querySelector('.bs-card__rate-count').textContent = '(' + count + ')';
    pill.setAttribute('aria-label', formatRating(value) + ' z 5, ' + count + ' ' + reviewsLabel(count));
  }
  media.appendChild(pill);
}

// Proužek zásoby: tři pásma podle počtu kusů (rozhodnutí Tomáše 2026-08-24 —
// návrh měl jen šířku a vždy zelenou, takže „1 ks" vypadalo stejně zdravě jako
// „>5 ks"). Mezistupně šířky by předstíraly přesnost, kterou `(N ks)` z výpisu
// nemá (Shoptet nad pěti kusy vrací jen „>5 ks").
//   ≥ 5 ks / „>5 ks" / bez čísla → zelená, 85 %
//   2–4 ks                       → žlutá,  50 %
//   1 ks                         → červená, 20 %
export function stockLevel(amount) {
  if (/>/.test(amount)) return { tier: 'ok', percent: 85 };
  const n = parseInt((amount.match(/(\d+)/) || [])[1], 10);
  if (!isFinite(n) || n >= 5) return { tier: 'ok', percent: 85 };
  if (n >= 2) return { tier: 'low', percent: 50 };
  return { tier: 'last', percent: 20 };
}

// „Ukazatel zásoby" (README §Karta produktu): nativní `.availability` doleva,
// vypočtená expedice doprava, pod tím proužek. Bez skladové informace (na
// objednávku, nedostupné) zůstává jen nativní řádek — nemá co ukazovat.
function enhanceAvailability(card) {
  const avail = card.querySelector('.availability');
  if (!avail || avail.dataset.bsAvail) return;
  avail.dataset.bsAvail = '1';

  const mark = document.createElement('span');
  mark.className = 'bs-card__stock-ic';
  mark.appendChild(icon('package'));
  avail.insertAdjacentElement('afterbegin', mark);

  const label = text(avail);
  const inStock = label.indexOf(LABEL_IN_STOCK) === 0;
  const amountEl = avail.querySelector('.availability-amount');
  const amount = text(amountEl);
  if (!inStock) return;

  // „(>5 ks)" → „>5 ks" — návrh závorky nemá
  if (amountEl) amountEl.textContent = amount.replace(/^\((.*)\)$/, '$1');

  avail.classList.add('is-in-stock');

  const stock = document.createElement('div');
  stock.className = 'bs-card__stock';
  const row = document.createElement('div');
  row.className = 'bs-card__stock-row';
  avail.insertAdjacentElement('beforebegin', stock);
  stock.appendChild(row);
  row.appendChild(avail);

  const ship = document.createElement('span');
  ship.className = 'bs-card__ship';
  const shipMark = document.createElement('span');
  shipMark.className = 'bs-card__ship-ic';
  shipMark.appendChild(icon('truck'));
  ship.appendChild(shipMark);
  ship.appendChild(document.createTextNode(listingDispatch().label));
  row.appendChild(ship);

  const level = stockLevel(amount);
  stock.classList.add('bs-card__stock--' + level.tier);
  const bar = document.createElement('span');
  bar.className = 'bs-card__bar';
  const fill = document.createElement('span');
  fill.className = 'bs-card__bar-fill';
  fill.style.width = level.percent + '%';
  bar.appendChild(fill);
  stock.appendChild(bar);
}

// Nativní submit karty. PŘIHLÁŠENÝ VELKOOBCHOD (ceník VO1–VO3) dostává ve výpisu
// jiný markup než anonym: tlačítko BEZ třídy `.add-to-cart-button` a za formem
// odkaz „Detail" (`a.secondary-to-cart-btn`). Bez třídy tlačítko chytal obecný
// restyle `#content-wrapper .btn` a karta ukazovala dvě červené pilulky pod
// sebou (ostrá 2026-09-13, fixture `dev/vo-category.html`). Třída se jen
// PŘIDÁVÁ: default JS ji nečte (košík jede přes submit `form.pr-action`), čtou
// ji naše moduly (styl karty, analytika, cartModal). „Detail" schová CSS —
// kartu prokliká fotka i název, návrh má jedno CTA.
function cartButton(card) {
  const btn =
    card.querySelector('.add-to-cart-button') ||
    card.querySelector('.p-tools form.pr-action button[type="submit"]');
  if (btn) btn.classList.add('add-to-cart-button');
  return btn;
}

// CTA: ikona + cena v popisku („Do košíku za 1 290 Kč"). Cenu schová CSS tam,
// kde se na kartu nevejde (mobilní mřížka 2 sloupce). Form se nesahá.
function enhanceCta(card) {
  const btn = cartButton(card);
  if (!btn || btn.querySelector('.bs-icon')) return;
  btn.insertAdjacentElement('afterbegin', icon('cart'));

  const label = btn.querySelector('span:not(.bs-icon)');
  const price = text(card.querySelector('.price-final strong')) || text(card.querySelector('.price-final'));
  if (!label || !price || label.querySelector('.bs-card__cta-price')) return;
  const extra = document.createElement('span');
  extra.className = 'bs-card__cta-price';
  extra.textContent = L.ctaSuffix(price);
  label.appendChild(extra);
}

function enhanceCard(card) {
  if (card.dataset.bsCard) return;
  card.dataset.bsCard = '1';

  // mini kartu spouští nativní blok „Nejprodávanější" (`.products-top`) nebo
  // náš vlastní obal `.bs-mini` (pás bestsellerů na 404, #33)
  const mini = !!card.closest('.products-top, .bs-mini');
  card.classList.add('bs-card');
  if (mini) card.classList.add('bs-card--mini');

  // mřížku dostane skutečný rodič `div.product` — Shoptet ho balí na každé
  // stránce jinak (#products / .product-slider > .products-block / .products-related)
  const item = card.closest('.product') || card;
  const grid = item.parentElement;
  if (grid && !grid.classList.contains('bs-card-grid')) {
    grid.classList.add('bs-card-grid');
    if (mini) grid.classList.add('bs-card-grid--mini');
  }

  // 1. obal fotky — jediný wrapper, který do Shoptet DOM přidáváme
  const image = card.querySelector('a.image');
  const media = document.createElement('div');
  media.className = 'bs-card__media';
  if (image) {
    image.insertAdjacentElement('beforebegin', media);
    media.appendChild(image);
    if (!image.querySelector('img')) media.appendChild(icon('photo', 'bs-card__ph'));
  } else {
    card.insertAdjacentElement('afterbegin', media);
    media.appendChild(icon('photo', 'bs-card__ph'));
  }

  // 2. badgy; Bestseller samolepka hned, když ji karta nese z indexu
  //    (výsledky hledání / harness), jinak ji doplní cardExtras.js či specChips.js
  buildBadges(card, media);
  if (card.dataset.bsBestseller === '1') mountSticker(card);

  // 3. cenová pilulka na fotce (mini karta má cenu v toku vedle názvu)
  if (!mini) {
    const prices = card.querySelector('.prices');
    if (prices) {
      prices.classList.add('bs-card__pill');
      media.appendChild(prices);
    }
  }

  // 4. hodnocení, dostupnost, CTA
  if (!mini) {
    buildRating(card, media);
    enhanceAvailability(card);
    enhanceCta(card);
  }
}

function enhanceAll(root) {
  (root || document).querySelectorAll(CARD_SELECTOR).forEach(enhanceCard);
}

// loadMore přidává karty AJAXem a ShoptetDOM* eventy u něj NEstřílí (viz CLAUDE.md)
function observe(block) {
  if (block.dataset.bsCardsObserved) return;
  block.dataset.bsCardsObserved = '1';
  new MutationObserver(function (muts) {
    muts.forEach(function (mu) {
      mu.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches(CARD_SELECTOR)) enhanceCard(node);
        else if (node.querySelectorAll) enhanceAll(node);
      });
    });
  }).observe(block, { childList: true, subtree: true });
}

// Slib expedice se počítá z aktuálního času — výpis otevřený přes uzávěrku
// expedice nebo půlnoc by jinak dál sliboval „dnes" vedle dosypaných karet se
// „zítra" (review 2026-09-09, B3-05). Přepočet po minutě a při návratu na
// záložku; zapisuje jen při změně (každý zápis je mutace pro observery).
let shipTickWired = false;

function refreshShipLabels() {
  const nodes = document.querySelectorAll('.bs-card__ship');
  if (!nodes.length) return;
  const label = listingDispatch().label;
  nodes.forEach(function (ship) {
    const last = ship.lastChild;
    if (last && last.nodeType === 3 && last.nodeValue !== label) last.nodeValue = label;
  });
}

function wireShipTick() {
  if (shipTickWired) return;
  shipTickWired = true;
  setInterval(refreshShipLabels, 60000);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refreshShipLabels();
  });
}

export function initProductCards() {
  enhanceAll(document);
  document.querySelectorAll('.products').forEach(observe);
  wireShipTick();
}

// Karty vzniklé AŽ PO initProductCards — mřížka vlastního vyhledávání (#67).
// `observeCards` stačí zavolat jednou na kontejner: dosypané karty (loadMore)
// se obléknou samy, stejně jako u nativního AJAX výpisu.
export function enhanceCards(root) {
  enhanceAll(root);
}

export function observeCards(block) {
  if (block) observe(block);
}
